import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Trash2, RotateCcw, Search, Loader2, FileText, Car, ClipboardCheck,
  ChevronLeft, ChevronRight, Calendar, X,
} from 'lucide-react';
import { adminAPI } from '../../lib/api';

const PAGE_SIZE = 20;

export default function DeletedRecordsLogPage() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  
  // Get current user role from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin';

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [recordType, setRecordType] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [deletedBy, setDeletedBy] = useState('');

  // Dialogs
  const [restoreDialog, setRestoreDialog] = useState({ open: false, item: null });
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState({ open: false, item: null });
  const [deleteReason, setDeleteReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Debounce search input
  const debounceRef = useRef(null);
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [entityType, recordType, fromDate, toDate, deletedBy]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (entityType !== 'all') params.entity_type = entityType;
      if (recordType !== 'all') params.record_type = recordType;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (deletedBy) params.deleted_by = deletedBy;

      const response = await adminAPI.getDeletedRecords(params);
      setRecords(response.data.records || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching deleted records:', error);
      toast.error('Failed to load deleted records');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, entityType, recordType, fromDate, toDate, deletedBy]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setEntityType('all');
    setRecordType('all');
    setFromDate('');
    setToDate('');
    setDeletedBy('');
    setPage(1);
  };

  const hasActiveFilters = debouncedSearch || entityType !== 'all' || recordType !== 'all' || fromDate || toDate || deletedBy;

  // Restore handler
  const handleRestore = async () => {
    if (!restoreDialog.item) return;
    setActionLoading(true);
    const item = restoreDialog.item;
    try {
      await adminAPI.restoreDeletedRecord(item.id, item.entity_type);
      toast.success(item.entity_type === 'record'
        ? 'Record and its inspections restored successfully'
        : 'Inspection restored successfully');
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore');
    } finally {
      setActionLoading(false);
      setRestoreDialog({ open: false, item: null });
    }
  };

  // Permanent delete handler
  const handlePermanentDelete = async () => {
    if (!permanentDeleteDialog.item || !deleteReason.trim()) {
      toast.error('Please provide a reason for permanent deletion');
      return;
    }
    setActionLoading(true);
    const item = permanentDeleteDialog.item;
    try {
      const response = await adminAPI.permanentDeleteRecord(item.id, item.entity_type, deleteReason);
      const data = response.data;
      toast.success(item.entity_type === 'record'
        ? `Record permanently deleted (${data.details?.inspections_deleted || 0} inspections, ${data.details?.cylinders_deleted || 0} cylinders removed)`
        : 'Inspection permanently deleted');
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete');
    } finally {
      setActionLoading(false);
      setPermanentDeleteDialog({ open: false, item: null });
      setDeleteReason('');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getRecordTypeLabel = (type) => {
    const labels = { car: 'Light Vehicle', '3w': '3-Wheeler', bus: 'Bus', truck: 'Truck', trailer: 'Trailer', cng_storage: 'CNG Storage' };
    return labels[type] || type || '-';
  };

  const getEntityBadge = (entityType) => {
    if (entityType === 'inspection') {
      return <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Inspection</Badge>;
    }
    return <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Record</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="deleted-records-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6" />
          Deleted Records Log
        </h1>
        <p className="text-muted-foreground">
          View and restore deleted records and inspections. All deletions are tracked with full metadata for regulatory compliance.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {/* Row 1: Search + Entity Type + Record Type */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search registration, verification, owner, inspector..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
                data-testid="deleted-search-input"
              />
            </div>

            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger className="w-[170px]" data-testid="entity-type-filter">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="record">Records Only</SelectItem>
                <SelectItem value="inspection">Inspections Only</SelectItem>
              </SelectContent>
            </Select>

            <Select value={recordType} onValueChange={setRecordType}>
              <SelectTrigger className="w-[170px]" data-testid="record-type-filter">
                <SelectValue placeholder="Record Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Record Types</SelectItem>
                <SelectItem value="car">Light Vehicle</SelectItem>
                <SelectItem value="3w">3-Wheeler</SelectItem>
                <SelectItem value="bus">Bus</SelectItem>
                <SelectItem value="truck">Truck</SelectItem>
                <SelectItem value="trailer">Trailer</SelectItem>
                <SelectItem value="cng_storage">CNG Storage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: Date range + Deleted By + Clear */}
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex gap-2 items-center">
              <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex gap-2 items-center">
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-[155px]"
                  placeholder="From date"
                  data-testid="from-date-filter"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-[155px]"
                  placeholder="To date"
                  data-testid="to-date-filter"
                />
              </div>
            </div>

            <div className="relative flex-1 max-w-[220px]">
              <Input
                placeholder="Deleted by..."
                value={deletedBy}
                onChange={(e) => setDeletedBy(e.target.value)}
                data-testid="deleted-by-filter"
              />
            </div>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Deleted Records Log ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span className="text-muted-foreground">Loading...</span>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Trash2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No deleted records found</p>
              {hasActiveFilters && <p className="text-sm mt-1">Try adjusting your filters</p>}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Record Type</TableHead>
                    <TableHead>Owner / Inspector</TableHead>
                    <TableHead>Deleted At</TableHead>
                    <TableHead>Deleted By</TableHead>
                    <TableHead>Reason</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((item) => {
                    const Icon = item.entity_type === 'inspection' ? ClipboardCheck : Car;
                    return (
                      <TableRow key={`${item.entity_type}-${item.id}`}>
                        <TableCell>{getEntityBadge(item.entity_type)}</TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            {item.identifier}
                          </div>
                          {item.entity_type === 'inspection' && item.registration_number && (
                            <div className="text-xs text-muted-foreground mt-1">Vehicle: {item.registration_number}</div>
                          )}
                        </TableCell>
                        <TableCell><Badge variant="outline">{getRecordTypeLabel(item.record_type)}</Badge></TableCell>
                        <TableCell>
                          {item.entity_type === 'record' ? (item.owner_name || '-') : (item.inspector_name || '-')}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(item.deleted_at)}</TableCell>
                        <TableCell><span className="text-sm font-medium">{item.deleted_by || '-'}</span></TableCell>
                        <TableCell className="max-w-[200px] truncate" title={item.deletion_reason}>
                          {item.deletion_reason || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {isAdmin && (
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => setRestoreDialog({ open: true, item })} data-testid={`restore-${item.id}`}>
                                <RotateCcw className="h-4 w-4 mr-1" /> Restore
                              </Button>
                              <Button variant="destructive" size="sm" onClick={() => setPermanentDeleteDialog({ open: true, item })} data-testid={`permanent-delete-${item.id}`}>
                                <Trash2 className="h-4 w-4 mr-1" /> Delete Forever
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t" data-testid="pagination-controls">
                  <span className="text-sm text-muted-foreground">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) { pageNum = i + 1; }
                      else if (page <= 3) { pageNum = i + 1; }
                      else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                      else { pageNum = page - 2 + i; }
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          data-testid={`page-${pageNum}-btn`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Restore Dialog */}
      <AlertDialog open={restoreDialog.open} onOpenChange={(open) => !open && setRestoreDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore {restoreDialog.item?.entity_type === 'inspection' ? 'Inspection' : 'Record'}?</AlertDialogTitle>
            <AlertDialogDescription>
              {restoreDialog.item?.entity_type === 'record' ? (
                <>This will restore the record <strong>{restoreDialog.item?.identifier}</strong> and all its associated inspections back to the system.</>
              ) : (
                <>This will restore the inspection <strong>{restoreDialog.item?.identifier}</strong> back to the system.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Dialog */}
      <AlertDialog open={permanentDeleteDialog.open} onOpenChange={(open) => { if (!open) { setPermanentDeleteDialog({ open: false, item: null }); setDeleteReason(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Permanently Delete {permanentDeleteDialog.item?.entity_type === 'inspection' ? 'Inspection' : 'Record'}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-destructive">
                <strong>Warning: This action is irreversible!</strong>
                <p className="mt-1 text-sm">
                  {permanentDeleteDialog.item?.entity_type === 'record' ? (
                    <>This will permanently delete the record <strong>{permanentDeleteDialog.item?.identifier}</strong>, along with ALL associated inspections and cylinder data.</>
                  ) : (
                    <>This will permanently delete the inspection <strong>{permanentDeleteDialog.item?.identifier}</strong>.</>
                  )}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Reason for permanent deletion <span className="text-destructive">*</span>
                </label>
                <Input placeholder="Enter reason for permanent deletion..." value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} className="w-full" data-testid="permanent-delete-reason" />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handlePermanentDelete} disabled={actionLoading || !deleteReason.trim()}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Forever
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
